export const UNASSIGNED_KEY = '__unassigned__'
export const UNGROUPED_KEY = '__ungrouped__'

export const TIME_TAG_VALUES = ['', 'any', 'morning', 'day', 'evening', 'night']

export const MEDIA_CATEGORY_VALUES = ['', 'selfie', 'everyday_photo', 'teaser', 'explicit', 'voice_phrase']

const SORT_ORDER_LAST = 999999

/** Готова ли группа к продаже (price_min/price_max заполнены и валидны). */
export function isGroupReadyForSale(priceMin, priceMax) {
  if (priceMin == null || priceMax == null) return false
  if (priceMin > priceMax) return false
  if (priceMin < 0 || priceMax > 200) return false
  return true
}

/** Какие поля группы нужно заполнить для продажи. */
export function getGroupMissingForSale(priceMin, priceMax) {
  if (priceMin == null && priceMax == null) return ['price']
  const missing = []
  if (priceMin == null) missing.push('price_min')
  if (priceMax == null) missing.push('price_max')
  if (priceMin != null && priceMax != null && priceMin > priceMax) missing.push('price_range')
  return missing
}

function sortOrderForKey(m) {
  const o = m.session_sort_order
  return o != null && Number.isFinite(o) ? o : SORT_ORDER_LAST
}

function groupSortOrderForKey(m) {
  const o = m.group_sort_order
  return o != null && Number.isFinite(o) ? o : SORT_ORDER_LAST
}

/**
 * Группирует медиа по сессиям и группам внутри сессий.
 * Результат: массив { key, name, session, mediaGroups } для каждой сессии,
 * где mediaGroups = [{ id, name, sort_order, media }].
 * Для обратной совместимости каждый элемент сессии также имеет .media — плоский список.
 */
export function groupMediaBySession(media, sessions) {
  const result = []
  const sessionMap = new Map()
  const unassigned = []

  // Инициализация: для каждой сессии — группы из API + виртуальная "без группы"
  sessions.forEach((s) => {
    const apiGroups = s.media_groups || []
    const groups = apiGroups.filter((g) => g.id != null).map((g) => ({
      id: g.id,
      name: g.name || '',
      description: g.description ?? '',
      sort_order: g.sort_order ?? 999999,
      price_min: g.price_min,
      price_max: g.price_max,
      is_ready_for_sale: g.is_ready_for_sale ?? false,
      missing_for_sale: g.missing_for_sale ?? [],
      media: [],
    }))
    groups.push({ id: null, name: null, sort_order: 999999, key: UNGROUPED_KEY, media: [] })
    sessionMap.set(s.id, { session: s, groups })
  })

  media.forEach((m) => {
    const sid = m.photo_session_id
    if (sid == null) {
      unassigned.push(m)
      return
    }
    const entry = sessionMap.get(sid)
    if (!entry) return
    const gid = m.media_group_id
    const targetGroup = gid != null
      ? entry.groups.find((g) => g.id === gid)
      : entry.groups.find((g) => g.id === null || g.key === UNGROUPED_KEY)
    if (targetGroup) targetGroup.media.push(m)
  })

  // Сортировка медиа внутри каждой группы
  sessionMap.forEach((entry, sessionId) => {
    entry.groups.forEach((grp) => {
      grp.media.sort((a, b) => {
        const orderKey = grp.id != null ? groupSortOrderForKey : sortOrderForKey
        const oa = orderKey(a)
        const ob = orderKey(b)
        if (oa !== ob) return oa - ob
        return (a.id ?? 0) - (b.id ?? 0)
      })
    })
    // Сортировка групп по sort_order
    entry.groups.sort((a, b) => (a.sort_order ?? 999999) - (b.sort_order ?? 999999))
  })

  sessions.forEach((s) => {
    const entry = sessionMap.get(s.id)
    if (entry) {
      const flatMedia = entry.groups.flatMap((g) => g.media)
      result.push({
        key: String(s.id),
        name: s.name,
        session: s,
        mediaGroups: entry.groups,
        media: flatMedia,
      })
    }
  })
  result.push({
    key: UNASSIGNED_KEY,
    name: null,
    session: null,
    mediaGroups: [{ id: null, name: null, sort_order: 0, media: unassigned }],
    media: unassigned,
  })
  return result
}

export function formatSyncUpdatedAt(isoStr) {
  if (!isoStr || typeof isoStr !== 'string') return null
  try {
    const d = new Date(isoStr)
    if (isNaN(d.getTime())) return null
    return d.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })
  } catch {
    return null
  }
}

export function shouldUseFullQualityThumbnail(item) {
  const type = String(item?.type || item?.media_type || '').toLowerCase()
  return type !== 'video' && type !== 'audio'
}

export function getMediaPreview(item, options = {}) {
  if (item?.id && (item.preview_path || item.media_path || item.preview_status === 'done' || item.media_status === 'done')) {
    return { local: true, mediaId: item.id }
  }
  if (item?.source === 'local') {
    return null
  }
  const files = item?.files
  if (!files) return null
  const preferFull = !!options.preferFull
  const candidates = preferFull
    ? [files.full, files.preview, files.squarePreview, files.square_preview, files.thumb]
    : [files.thumb, files.preview, files.squarePreview, files.square_preview, files.full]
  const f = candidates.find(Boolean)
  const url = f?.url ?? null
  if (!url) return null
  const isImage =
    /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(url) ||
    item?.type === 'photo' ||
    item?.type === 'gif' ||
    item?.type === 'video'
  return isImage ? { cdnUrl: url } : null
}
